import type { ReviewEntryItem } from '../types/reviewEntryItem'
import { getMatchedVatRateId, MatchableTaxRate } from './getMatchedVatRateId'
import { hasClarificationBadge } from './hasClarificationBadge'
import { isToValidateStatus } from './isToValidateStatus'

interface GetReviewEntryValidationFlagsOptions {
  item: ReviewEntryItem
  // supplier contact resolved upstream — from `processingDecision` or the name-matching fallback
  supplierId: string | null
  vatRates: MatchableTaxRate[]
}

export interface ReviewEntryValidationFlags {
  isAutomatched: boolean
  isSupplierResolved: boolean
  // the entry can be validated as-is: pending status, resolved supplier and resolved VAT.
  // outstanding questions do NOT block validation
  isValidatable: boolean
  isVatResolved: boolean
}

// every line must resolve to a known purchase VAT rate — an unresolved rate means
// the advisor still has to adjust the VAT manually, so the entry cannot be validated as-is
const hasResolvedVatRates = (item: ReviewEntryItem, vatRates: MatchableTaxRate[]): boolean => {
  const entryLines = item.entryLines ?? []

  if (!entryLines.length) {
    return false
  }

  return entryLines.every(
    (line) => getMatchedVatRateId({ taxRateId: line.taxRateId, taxRateLabel: line.taxRateLabel, vatRates }) !== '',
  )
}

export const getReviewEntryValidationFlags = ({
  item,
  supplierId,
  vatRates,
}: GetReviewEntryValidationFlagsOptions): ReviewEntryValidationFlags => {
  const isSupplierResolved = supplierId !== null
  const isVatResolved = hasResolvedVatRates(item, vatRates)
  // same status source as the table rows (`flattenReviewEntriesToTableRows`), so an entry
  // never looks validatable in the UI while being rejected here (or the opposite)
  const isValidatable =
    Boolean(item.documentId) &&
    isToValidateStatus(item.documentStatus ?? item.status) &&
    isSupplierResolved &&
    isVatResolved

  return {
    isAutomatched: isValidatable && !hasClarificationBadge(item.questions ?? []),
    isSupplierResolved,
    isValidatable,
    isVatResolved,
  }
}
